/*
 * PlainLanguageChecker
 * Checks the opening of a practice-area page against the article's advice: write for someone
 * who has never spoken to a lawyer.
 *
 * Rules (run on the pasted text, case-insensitive, whole words/phrases only):
 * 1. Jargon: each term in JARGON that appears is listed once with a plain alternative.
 * 2. Speaks to the reader: passes if the text contains "you" or "your".
 * 3. Next step: passes if it contains any of: call, contact, book, schedule, consultation,
 *    "talk to", "speak with", "next step", "what to do".
 * 4. Cost: passes if it contains any of: cost, costs, fee, fees, price, pay, charge, afford.
 * 5. Sentence length: average words per sentence (split on . ! ?) above 25 is flagged.
 * Nothing is sent, stored or tracked; analysis happens in the browser as the reader types.
 */
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";

const JARGON: { term: string; plain: string }[] = [
  { term: "tort", plain: "a claim for harm someone caused you" },
  { term: "tortfeasor", plain: "the person who caused the harm" },
  { term: "negligence", plain: "carelessness" },
  { term: "liability", plain: "who is responsible" },
  { term: "plaintiff", plain: "the person bringing the claim (you)" },
  { term: "defendant", plain: "the person or company being sued" },
  { term: "litigation", plain: "a lawsuit" },
  { term: "adjudicate", plain: "decide" },
  { term: "pursuant to", plain: "under" },
  { term: "herein", plain: "here / in this page" },
  { term: "hereinafter", plain: "from now on" },
  { term: "aforementioned", plain: "mentioned above" },
  { term: "remuneration", plain: "pay" },
  { term: "indemnify", plain: "cover the cost of" },
  { term: "jurisdiction", plain: "where the case can be heard" },
  { term: "statute of limitations", plain: "the deadline to file" },
  { term: "subrogation", plain: "your insurer getting its money back" },
  { term: "deposition", plain: "a recorded interview under oath" },
  { term: "prima facie", plain: "at first sight" },
  { term: "mens rea", plain: "intent" },
  { term: "pro se", plain: "representing yourself" },
  { term: "res ipsa loquitur", plain: "the facts speak for themselves" },
  { term: "habeas corpus", plain: "a challenge to being held in custody" },
  { term: "counsel", plain: "a lawyer" },
];

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const has = (text: string, words: string[]) =>
  words.some((w) => new RegExp(`\\b${escape(w)}\\b`, "i").test(text));

export default function PlainLanguageChecker() {
  const id = useId();
  const [text, setText] = useState("");
  const trimmed = text.trim();

  const jargonFound = JARGON.filter((j) => new RegExp(`\\b${escape(j.term)}\\b`, "i").test(trimmed));
  const sentences = trimmed.split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
  const words = trimmed.split(/\s+/).filter(Boolean).length;
  const avgWords = sentences.length > 0 ? Math.round(words / sentences.length) : 0;

  const checks = [
    {
      label: "Speaks to the reader (“you”, “your”)",
      pass: has(trimmed, ["you", "your"]),
      fix: "Address the reader directly. “If you were hurt in a crash…” reads very differently from “Injured parties may…”.",
    },
    {
      label: "Gives a next step",
      pass: has(trimmed, ["call", "contact", "book", "schedule", "consultation", "talk to", "speak with", "next step", "what to do"]),
      fix: "Tell them what to do now: who to call, what happens in a consultation, or what to do in the next few days.",
    },
    {
      label: "Mentions cost",
      pass: has(trimmed, ["cost", "costs", "fee", "fees", "price", "pay", "charge", "afford"]),
      fix: "Say how fees work, in a sentence. Cost is one of the first things a worried reader wants to know.",
    },
    {
      label: "Sentences are a readable length",
      pass: avgWords <= 25,
      fix: `Your sentences average about ${avgWords} words. Break the longest ones up.`,
    },
  ];

  const passed = checks.filter((c) => c.pass).length;
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Plain-language checker for your practice-area page</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Paste the first paragraph or two of one of your practice-area pages. Nothing is saved or sent anywhere.
      </p>

      <label htmlFor={id} className="mt-4 block text-sm font-medium">
        Your page opening
      </label>
      <textarea
        id={id}
        rows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. If you were hurt in a car accident, here's what to do in the next few days…"
        className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
      />

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {trimmed.length === 0 ? (
          <p className="text-sm text-muted-foreground">Results will appear here as you paste or type.</p>
        ) : (
          <div>
            <p className="text-sm font-semibold">
              {passed} of {checks.length} reader checks passed
              {jargonFound.length > 0 ? `, ${jargonFound.length} legal term${jargonFound.length === 1 ? "" : "s"} found` : ", no legal jargon found"}
            </p>

            <ul className="mt-3 space-y-2">
              {checks.map((c) => (
                <li key={c.label} className="rounded-md border border-border bg-card p-3 text-sm">
                  <p className="font-medium">
                    <span aria-hidden="true">{c.pass ? "✓ " : "✕ "}</span>
                    <span className="sr-only">{c.pass ? "Passed: " : "Needs work: "}</span>
                    {c.label}
                  </p>
                  {!c.pass && <p className="mt-1 text-muted-foreground">{c.fix}</p>}
                </li>
              ))}
            </ul>

            {jargonFound.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold">Legal terms to swap or explain</p>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                  {jargonFound.map((j) => (
                    <li key={j.term} className="rounded-md border border-border bg-card p-3 text-sm">
                      <span className="font-medium">“{j.term}”</span>
                      <span className="text-muted-foreground"> → {j.plain}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="mt-4 text-sm">
              Want every practice-area page written this way?{" "}
              <Link to={"/services/legal-content-writing" as string} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Legal content writing →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
